/**
 * useMassUpdate - Centralized mass update hook for all ORGANIZATIONS_TABLE entities
 *
 * This hook handles mass updates for organizations, deals, and products with:
 * - Consistent ID matching (supports PK, SK, org_uuid, deal_uuid)
 * - Direct grid row updates via apiRef (instant UI feedback)
 * - Centralized cache updates for both SEARCH_DATA_KEY and server-side search queries
 * - Unified error handling and snackbar notifications
 *
 * Usage:
 * ```tsx
 * const apiRef = useGridApiRef()
 * const { handleMassUpdate, isUpdating } = useMassUpdate({
 *   entityType: 'organization',
 *   context: 'organizations',
 *   apiRef, // Pass apiRef for instant grid updates
 * })
 *
 * // In your update handler:
 * handleMassUpdate(massUpdateData, rowSelectionModel)
 * ```
 */

import { GridApiPremium, GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { useQueryClient } from '@tanstack/react-query'
import { useBatchUpdateItems } from 'api/aws/organizations_table/useBatchUpdateItems'
import { SEARCH_DATA_KEY, useEntitiesFromSearch } from 'api/search'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { MutableRefObject, useCallback, useMemo, useState } from 'react'
import { DynamicMassUpdateData, OrganizationTableItem } from 'types'

// Entity type configuration
type EntityType = 'organization' | 'deal'

// Grid context determines which server-side cache keys to update
type GridContext = 'organizations' | 'mtb' | 'deals' | 'nco' | 'products'

/** Resolver data for building display-ready grid updates */
interface ResolverOptions {
  users?: any[]
  contacts?: any[]
  organizations?: any[]
}

interface UseMassUpdateOptions {
  entityType: EntityType
  context: GridContext
  /** Pass apiRef for instant grid row updates without full re-render */
  apiRef?: MutableRefObject<GridApiPremium>
  /** Pass resolver data to build display-ready grid updates (e.g., resolve user IDs to user objects) */
  resolvers?: ResolverOptions
}

// Fields that should always remain as arrays
const ARRAY_FIELDS = [
  'team_details',
  'affiliated',
  'contractedBy',
  'primaryContact',
  'billingContact',
  'contacts',
  'owner',
]

// Team role fields that should store user_uuids (strings), not full user objects
const TEAM_ROLE_FIELDS = ['team.AM', 'team.PM', 'team.DMS', 'team.SEO', 'team.MIA', 'team.BDE', 'team.QA', 'team.other']

// Contact fields that should store contacts_uuids (strings), not full contact objects
const CONTACT_FIELDS = ['primaryContact', 'billingContact', 'contacts']

// Org relationship fields that should store org_uuids (strings), not full org objects
const ORG_RELATIONSHIP_FIELDS = ['contractedBy', 'affiliated']

// Owner field stores user_uuid or slack ID
const OWNER_FIELD = 'owner'

// Map entity types to their data source and ID fields
const ENTITY_CONFIG = {
  organization: {
    dataKey: 'organizations' as const,
    tableName: 'ORGANIZATIONS_TABLE',
    primaryIdField: 'PK',
    secondaryIdField: 'org_uuid',
    sortKeyField: 'SK',
    singularName: 'organization',
    pluralName: 'organizations',
    // Which server-side search query keys to update
    searchKeys: ['organizations', 'mtb'],
  },
  deal: {
    dataKey: 'deals' as const,
    tableName: 'ORGANIZATIONS_TABLE',
    primaryIdField: 'SK', // Deals are identified by SK
    secondaryIdField: 'deal_uuid',
    sortKeyField: 'SK',
    singularName: 'deal',
    pluralName: 'deals',
    searchKeys: ['deals', 'nco'],
  },
}

/**
 * Normalize mass update values - handle array fields consistently
 * Extracts UUIDs from full objects when needed
 */
const normalizeValue = (field: string, value: any): any => {
  // Team role fields (team.AM, team.PM, etc.) store user_uuids, not full objects
  if (TEAM_ROLE_FIELDS.includes(field)) {
    if (!Array.isArray(value)) return value !== undefined ? [] : []
    return value.map((item: any) => {
      if (typeof item === 'string') return item
      if (item?.user_uuid) return item.user_uuid
      return item
    })
  }

  // Contact fields store contacts_uuids, not full contact objects
  if (CONTACT_FIELDS.includes(field)) {
    if (!Array.isArray(value)) {
      // Single contact - extract UUID
      if (typeof value === 'string') return [value]
      if (value?.contacts_uuid) return [value.contacts_uuid]
      return value !== undefined ? [] : []
    }
    return value.map((item: any) => {
      if (typeof item === 'string') return item
      if (item?.contacts_uuid) return item.contacts_uuid
      return item
    })
  }

  // Org relationship fields store org_uuids, not full org objects
  if (ORG_RELATIONSHIP_FIELDS.includes(field)) {
    if (!Array.isArray(value)) {
      // Single org - extract UUID
      if (typeof value === 'string') return [value]
      if (value?.org_uuid) return [value.org_uuid]
      return value !== undefined ? [] : []
    }
    return value.map((item: any) => {
      if (typeof item === 'string') return item
      if (item?.org_uuid) return item.org_uuid
      return item
    })
  }

  // Owner field - single user_uuid or slack ID
  if (field === OWNER_FIELD) {
    if (Array.isArray(value)) {
      const first = value[0]
      if (typeof first === 'string') return [first]
      if (first?.user_uuid) return [first.user_uuid]
      if (first?.id) return [first.id] // Slack ID fallback
      return value
    }
    if (typeof value === 'string') return [value]
    if (value?.user_uuid) return [value.user_uuid]
    if (value?.id) return [value.id]
    return value
  }

  // Other array fields - keep as arrays
  if (ARRAY_FIELDS.includes(field)) {
    return Array.isArray(value) ? value : value !== undefined ? [value] : []
  }

  // For other fields, flatten single-item arrays
  if (Array.isArray(value) && value.length === 1) {
    return value[0]
  }
  return value
}

/**
 * Set a nested value in an object using dot notation path
 * e.g., setNestedValue(obj, 'team.AM', value) sets obj.team.AM = value
 * For object values, merges with existing object instead of replacing
 */
const setNestedValue = (obj: Record<string, any>, path: string, value: any): Record<string, any> => {
  const keys = path.split('.')
  if (keys.length === 1) {
    // Simple key, no nesting
    // If value is an object (not array) and existing value is also object, merge them
    const existingValue = obj[path]
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      existingValue &&
      typeof existingValue === 'object' &&
      !Array.isArray(existingValue)
    ) {
      return { ...obj, [path]: { ...existingValue, ...value } }
    }
    return { ...obj, [path]: value }
  }

  // Deep clone and set nested value
  const result = { ...obj }
  let current: any = result

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    current[key] = { ...current[key] }
    current = current[key]
  }

  current[keys[keys.length - 1]] = value
  return result
}

/**
 * Apply multiple field updates to an object, handling nested paths
 */
const applyFieldUpdates = (obj: Record<string, any>, fieldsToUpdate: Record<string, any>): Record<string, any> => {
  let result = { ...obj }
  for (const [path, value] of Object.entries(fieldsToUpdate)) {
    result = setNestedValue(result, path, value)
  }
  return result
}

/**
 * Check if a value is valid for update
 */
const isValidValue = (value: any): boolean => {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'boolean') return true
  if (typeof value === 'number') return true
  return false
}

/**
 * Find an entity by various ID formats
 */
const findEntity = <T extends OrganizationTableItem>(
  entities: T[],
  id: string | number,
  config: typeof ENTITY_CONFIG[EntityType]
): T | undefined => {
  const idStr = String(id)

  // Try to extract SK from composite ID format "PK::SK"
  const idParts = idStr.split('::')
  const possibleSK = idParts.length === 2 ? idParts[1] : idStr

  return entities.find(entity => {
    // Match by primary ID field (PK for orgs, SK for deals)
    if (entity[config.primaryIdField] === idStr) return true
    // Match by secondary ID field (org_uuid, deal_uuid)
    if (config.secondaryIdField && entity[config.secondaryIdField] === idStr) return true
    // For deals, also try matching the extracted SK
    if (config.dataKey === 'deals' && entity.SK === possibleSK) return true
    // For orgs, also try matching by org_uuid with the raw id
    if (config.dataKey === 'organizations' && entity.org_uuid === idStr) return true
    return false
  })
}

export const useMassUpdate = (options: UseMassUpdateOptions) => {
  const { entityType, context, apiRef, resolvers } = options
  const config = ENTITY_CONFIG[entityType]

  const { showSnackbar } = useSnackbarContext()
  const [isUpdating, setIsUpdating] = useState(false)
  const queryClient = useQueryClient()
  const { mutate: batchUpdate } = useBatchUpdateItems()
  const searchData = useEntitiesFromSearch()

  // Get the appropriate data array based on entity type
  const entities = searchData[config.dataKey] as OrganizationTableItem[]

  // Build lookup maps for resolving IDs to display objects
  const userMap = useMemo(() => {
    const map = new Map<string, any>()
    resolvers?.users?.forEach(u => {
      if (u.user_uuid) map.set(u.user_uuid, u)
      if (u.id) map.set(u.id, u) // Also map by Slack ID
    })
    return map
  }, [resolvers?.users])

  const contactMap = useMemo(() => {
    const map = new Map<string, any>()
    resolvers?.contacts?.forEach(c => {
      if (c.contacts_uuid) map.set(c.contacts_uuid, c)
    })
    return map
  }, [resolvers?.contacts])

  const orgMap = useMemo(() => {
    const map = new Map<string, any>()
    // Use resolvers if provided, otherwise fall back to entities from searchData
    const orgs = resolvers?.organizations?.length ? resolvers.organizations : entities
    orgs?.forEach((o: any) => {
      if (o.org_uuid) map.set(o.org_uuid, o)
    })
    return map
  }, [resolvers?.organizations, entities])

  /**
   * Build resolved display values for grid row updates
   * This ensures _resolvedTeam, _resolvedOwner, etc. are updated for immediate display
   */
  const buildResolvedUpdates = useCallback(
    (normalizedFields: Record<string, any>): Record<string, any> => {
      const resolved: Record<string, any> = {}

      for (const [field, value] of Object.entries(normalizedFields)) {
        // Team role fields (team.AM, team.PM, etc.)
        if (TEAM_ROLE_FIELDS.includes(field)) {
          const roleName = field.replace('team.', '') // 'AM', 'PM', etc.
          const userIds = Array.isArray(value) ? value : value ? [value] : []
          const resolvedUsers = userIds.map(id => userMap.get(id)).filter(Boolean)

          // Update both the raw team data and the resolved display data
          if (!resolved.team) resolved.team = {}
          if (!resolved._resolvedTeam) resolved._resolvedTeam = {}
          resolved.team[roleName] = userIds
          resolved._resolvedTeam[roleName] = resolvedUsers
        }
        // Owner field
        else if (field === OWNER_FIELD) {
          const ownerIds = Array.isArray(value) ? value : value ? [value] : []
          const resolvedOwner = ownerIds.length > 0 ? userMap.get(ownerIds[0]) : null
          resolved.owner = ownerIds
          resolved._resolvedOwner = resolvedOwner
        }
        // Contact fields
        else if (CONTACT_FIELDS.includes(field)) {
          const contactIds = Array.isArray(value) ? value : value ? [value] : []
          const resolvedContacts = contactIds.map(id => contactMap.get(id)).filter(Boolean)

          if (field === 'primaryContact') {
            resolved.primaryContact = contactIds
            resolved._resolvedPrimaryContact = resolvedContacts[0] || null
          } else if (field === 'contacts') {
            resolved.contacts = contactIds
            resolved.relatedContacts = resolvedContacts
          } else {
            resolved[field] = contactIds
          }
        }
        // Org relationship fields
        else if (ORG_RELATIONSHIP_FIELDS.includes(field)) {
          const orgIds = Array.isArray(value) ? value : value ? [value] : []
          const resolvedOrgs = orgIds.map(id => orgMap.get(id)).filter(Boolean)

          if (field === 'contractedBy') {
            resolved.contractedBy = resolvedOrgs.length > 0 ? resolvedOrgs : orgIds
            resolved._resolvedContractedBy = resolvedOrgs
          } else if (field === 'affiliated') {
            resolved.affiliated = resolvedOrgs.length > 0 ? resolvedOrgs : orgIds
            resolved._resolvedAffiliated = resolvedOrgs
          }
        }
        // Other fields - pass through as-is
        else {
          resolved[field] = value
        }
      }

      return resolved
    },
    [userMap, contactMap, orgMap]
  )

  /**
   * Directly update grid rows via apiRef (instant UI feedback)
   * This bypasses React's re-render cycle for immediate visual updates
   */
  const updateGridRowsDirectly = useCallback(
    (rowUpdates: Array<{ id: string | number; [key: string]: any }>) => {
      console.log('[useMassUpdate] updateGridRowsDirectly called with', rowUpdates.length, 'updates')
      console.log('[useMassUpdate] apiRef available:', !!apiRef?.current)
      console.log('[useMassUpdate] updateRows available:', !!apiRef?.current?.updateRows)

      if (!apiRef?.current?.updateRows) {
        console.warn('[useMassUpdate] No apiRef.updateRows available, skipping direct grid update')
        return
      }

      try {
        // Log the first update to see format
        // console.log('[useMassUpdate] Sample row update:', JSON.stringify(rowUpdates[0], null, 2))
        console.log('[useMassUpdate] Calling apiRef.current.updateRows with', rowUpdates.length, 'rows')
        apiRef.current.updateRows(rowUpdates)
        console.log('[useMassUpdate] updateRows completed successfully')
      } catch (error) {
        console.error('[useMassUpdate] Failed to update grid rows directly:', error)
      }
    },
    [apiRef]
  )

  /**
   * Update the React Query cache optimistically
   */
  const updateCache = useCallback(
    (updatedEntities: OrganizationTableItem[], fieldsToUpdate: Record<string, any>) => {
      // Build multiple lookup maps for flexible matching
      // Key by PK, org_uuid/deal_uuid, and composite PK::SK
      const byPrimaryId = new Map(updatedEntities.map(e => [e[config.primaryIdField], e]))
      const bySecondaryId = new Map(
        updatedEntities.filter(e => e[config.secondaryIdField]).map(e => [e[config.secondaryIdField], e])
      )
      const byCompositeKey = new Map(updatedEntities.filter(e => e.PK && e.SK).map(e => [`${e.PK}::${e.SK}`, e]))

      // Find matching entity from any of the maps
      const findUpdatedEntity = (row: any): OrganizationTableItem | undefined => {
        // Try primary ID (PK for orgs, SK for deals)
        let match = byPrimaryId.get(row[config.primaryIdField])
        if (match) return match

        // Try secondary ID (org_uuid, deal_uuid)
        match = bySecondaryId.get(row[config.secondaryIdField])
        if (match) return match

        // Try composite key
        if (row.PK && row.SK) {
          match = byCompositeKey.get(`${row.PK}::${row.SK}`)
          if (match) return match
        }

        // Try matching by 'id' field (grid row ID)
        if (row.id) {
          match = bySecondaryId.get(row.id) || byPrimaryId.get(row.id)
          if (match) return match
        }

        return undefined
      }

      console.log(
        '[useMassUpdate] Updating cache with',
        updatedEntities.length,
        'entities, fields:',
        Object.keys(fieldsToUpdate)
      )

      // 1) Update main SEARCH_DATA_KEY cache
      queryClient.setQueryData(SEARCH_DATA_KEY, (oldData: any) => {
        if (!oldData?.[config.dataKey]) return oldData

        const updatedArray = oldData[config.dataKey].map((item: OrganizationTableItem) => {
          const match = findUpdatedEntity(item)
          // Use applyFieldUpdates to properly handle nested paths like 'team.AM'
          return match ? applyFieldUpdates(item, fieldsToUpdate) : item
        })

        return {
          ...oldData,
          [config.dataKey]: updatedArray,
        }
      })

      // 2) Update all matching server-side search queries
      const queriesUpdated: string[] = []
      queryClient.setQueriesData(
        {
          predicate: q =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'search' &&
            config.searchKeys.includes(q.queryKey[1] as string),
        },
        (oldData: any) => {
          if (!oldData?.rows) return oldData

          const queryKeyStr = JSON.stringify(oldData)?.slice(0, 100)
          queriesUpdated.push(queryKeyStr)

          let matchCount = 0
          const newRows = oldData.rows.map((row: any) => {
            const match = findUpdatedEntity(row)
            if (match) {
              matchCount++
              // Use applyFieldUpdates to properly handle nested paths like 'team.AM'
              return applyFieldUpdates(row, fieldsToUpdate)
            }
            return row
          })

          console.log('[useMassUpdate] Updated', matchCount, 'rows in query')

          return {
            ...oldData,
            rows: newRows,
          }
        }
      )

      console.log('[useMassUpdate] Updated', queriesUpdated.length, 'queries')
    },
    [queryClient, config]
  )

  /**
   * Handle mass update for selected rows
   */
  const handleMassUpdate = useCallback(
    (massUpdateData: DynamicMassUpdateData, selectionModel: GridRowSelectionModel) => {
      if (!selectionModel?.ids?.size) {
        showSnackbar(`No ${config.pluralName} selected for update`, 'error')
        return
      }

      setIsUpdating(true)

      // Build update payloads
      const updates = Object.entries(massUpdateData).flatMap(([field, value]) => {
        const normalizedValue = normalizeValue(field, value)

        if (!isValidValue(normalizedValue)) return []

        return Array.from(selectionModel.ids)
          .map(id => {
            const entity = findEntity(entities, id, config)
            if (!entity) {
              console.warn(`[useMassUpdate] Entity not found for ID: ${id}`)
              return null
            }

            return {
              tableName: config.tableName,
              PK: entity.PK,
              SK: entity.SK,
              fieldName: field,
              newValue: normalizedValue,
            }
          })
          .filter(Boolean)
      })

      if (updates.length === 0) {
        showSnackbar('No valid updates to apply', 'error')
        setIsUpdating(false)
        return
      }

      // Build updated entities for cache sync
      const updatedEntitiesMap: Record<string, OrganizationTableItem> = {}

      updates.forEach(update => {
        if (!update) return
        const { PK, SK, fieldName, newValue } = update
        const key = `${PK}::${SK}`
        const existing = entities.find(e => e.PK === PK && e.SK === SK)

        if (!existing) return

        if (!updatedEntitiesMap[key]) {
          updatedEntitiesMap[key] = { ...existing }
        }
        updatedEntitiesMap[key][fieldName] = newValue
      })

      const updatedEntities = Object.values(updatedEntitiesMap)

      // Build normalized fields object for cache update
      const normalizedFields: Record<string, any> = {}
      Object.entries(massUpdateData).forEach(([field, value]) => {
        const normalizedValue = normalizeValue(field, value)
        if (isValidValue(normalizedValue)) {
          normalizedFields[field] = normalizedValue
        }
      })

      console.log(
        `[useMassUpdate] Sending ${updates.length} updates for ${updatedEntities.length} ${config.pluralName}`
      )

      // Build resolved updates for grid display (includes _resolvedTeam, _resolvedOwner, etc.)
      const resolvedUpdates = buildResolvedUpdates(normalizedFields)

      // Build row updates for direct grid update
      // GlobalDataGrid uses getRowId = r => r.rowId, so we include both 'id' (for TypeScript/API)
      // and 'rowId' (for getRowId matching). MUI's updateRows uses getRowIdFromRowModel internally.
      const gridRowUpdates = Array.from(selectionModel.ids).map(selectionId => ({
        id: selectionId as string | number, // Satisfies TypeScript type for updateRows API
        rowId: selectionId, // Used by getRowId = r => r.rowId to find the row
        ...normalizedFields, // Raw field values for backend/cache
        ...resolvedUpdates, // Resolved display values for UI
      }))

      // Send update mutation
      batchUpdate(updates as any, {
        onSuccess: () => {
          setIsUpdating(false)
          showSnackbar(
            selectionModel.ids.size === 1
              ? `${config.singularName.charAt(0).toUpperCase() + config.singularName.slice(1)} updated successfully`
              : `${selectionModel.ids.size} ${config.pluralName} updated successfully`,
            'success'
          )

          // 1) INSTANT: Update grid rows directly via apiRef (immediate visual feedback)
          updateGridRowsDirectly(gridRowUpdates)

          // 2) BACKGROUND: Update React Query caches for consistency
          // Include both raw fields AND resolved fields so cache has display-ready data
          const cacheUpdates = { ...normalizedFields, ...resolvedUpdates }
          updateCache(updatedEntities, cacheUpdates)
        },
        onError: error => {
          setIsUpdating(false)
          console.error('[useMassUpdate] Error:', error)
          showSnackbar('Mass update failed', 'error')
        },
      })
    },
    [entities, config, showSnackbar, batchUpdate, updateCache, updateGridRowsDirectly, buildResolvedUpdates]
  )

  return {
    handleMassUpdate,
    isUpdating,
  }
}

export default useMassUpdate
